import { clearAllData, getSettings } from "../db";
import { downloadExcelBackup } from "../excel/export";
import { applyReplaceImport, parseExcelFile, type ImportPreview } from "../excel/import";
import { ensureXlsx } from "../excel/loader";
import type { Route } from "../types";
import { uiConfirm } from "../ui/dialog";
import { getViewEl } from "../ui/layout";
import { esc, toast } from "../ui/toast";

function formatTs(iso: string | null): string {
  if (!iso) {
    return "尚未备份";
  }
  return iso.slice(0, 19).replace("T", " ");
}

export async function renderDataPage(route: Route): Promise<void> {
  void ensureXlsx().catch(() => {});
  const view = getViewEl();
  if (!view) {
    return;
  }

  const settings = await getSettings();
  const importFocus = route.focus === "import";

  view.innerHTML = `
    <div class="page-head">
      <div>
        <h1>数据</h1>
        <p class="lead">用 Excel 备份与恢复。数据只保存在本浏览器。</p>
      </div>
    </div>
    <div class="panel data-panel">
      <section class="data-section" id="sec-export">
        <h2>导出</h2>
        <p class="lead">上次备份：${esc(formatTs(settings.lastBackupAt))}。文件含阅读记录、诊断块、学习步骤与设置。</p>
        <button type="button" class="btn" id="btnExport">导出 Excel</button>
      </section>
      <section class="data-section${importFocus ? " data-section-focus" : ""}" id="sec-import">
        <h2>导入</h2>
        <p class="lead">选择本应用导出的 Excel 文件，完整替换本机数据。</p>
        <input type="file" id="fileImport" class="hidden-file" accept=".xlsx,.xls" />
        <button type="button" class="btn${importFocus ? " btn-primary" : ""}" id="btnPick">选择文件</button>
        <div id="importPreview"></div>
      </section>
      <section class="data-section" id="sec-clear">
        <h2>清空</h2>
        <p class="lead">删除本机全部阅读数据与设置。不可恢复，请先导出。</p>
        <button type="button" class="btn btn-danger" id="btnClearAll">清空所有数据</button>
      </section>
    </div>
  `;

  view.querySelector<HTMLButtonElement>("#btnExport")?.addEventListener("click", async () => {
    try {
      await downloadExcelBackup();
      toast("已导出");
      await renderDataPage(route);
    } catch (err) {
      toast(err instanceof Error ? err.message : "导出失败");
    }
  });

  const fileInput = view.querySelector<HTMLInputElement>("#fileImport");
  view.querySelector<HTMLButtonElement>("#btnPick")?.addEventListener("click", () => {
    fileInput?.click();
  });

  fileInput?.addEventListener("change", async () => {
    const file = fileInput.files?.[0];
    if (!file) {
      return;
    }
    await previewImport(file);
    fileInput.value = "";
  });

  view.querySelector<HTMLButtonElement>("#btnClearAll")?.addEventListener("click", async () => {
    const ok = await uiConfirm({
      title: "清空数据",
      message: "确定清空本机全部数据？此操作不可恢复。",
      confirmLabel: "清空",
      danger: true,
    });
    if (!ok) {
      return;
    }
    const again = await uiConfirm({
      title: "再次确认",
      message: "清空后浏览器不再保留你的阅读数据。",
      confirmLabel: "确认清空",
      danger: true,
    });
    if (!again) {
      return;
    }
    try {
      await clearAllData();
      toast("已清空本机数据");
      location.hash = "#/read";
    } catch (err) {
      toast(err instanceof Error ? err.message : "清空失败");
    }
  });

  const focusId =
    route.focus === "import" ? "sec-import" : route.focus === "export" ? "sec-export" : null;
  if (focusId) {
    view.querySelector(`#${focusId}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

async function previewImport(file: File): Promise<void> {
  const box = document.getElementById("importPreview");
  if (!box) {
    return;
  }

  try {
    const parsed: ImportPreview = await parseExcelFile(file);
    const readingCount = parsed.readings.length;
    const stepCount = parsed.learningSteps.length;

    box.innerHTML = `
      <p class="import-status">将写入 ${readingCount} 条阅读、${stepCount} 个学习步骤，并清空本机现有数据。</p>
      <p class="import-replace-hint">完整替换会先清空本机现有数据再写入。</p>
      <button type="button" class="btn btn-primary" id="btnApplyImport">确认导入</button>
    `;

    box.querySelector<HTMLButtonElement>("#btnApplyImport")?.addEventListener("click", async () => {
      try {
        await applyReplaceImport(parsed);
        toast("导入完成");
        location.hash = "#/library";
      } catch (err) {
        toast(err instanceof Error ? err.message : "导入失败");
      }
    });
  } catch (err) {
    box.innerHTML = `<p class="import-status import-status-error">${esc(err instanceof Error ? err.message : String(err))}</p>`;
  }
}
